// Reports a file Import files couldn't read or save, so the failure can be reproduced from
// the file itself (admin → Import failures, and a Slack message). The user is asked first,
// once per batch: on "Send" the file goes with the report; on "Don't send" the report goes
// without it, marked `fileDeclined`. Sending never holds up the import, and any failure of
// its own is dropped. Same codes as the apps:
//   - read: `not_readable` (damaged, or not what its extension says). `empty` (nothing in it,
//     however well-formed) and `unsupported_format` (a PDF, say) aren't sent — the error
//     already says everything the file would.
//   - save: `save_failed`, with the error as the app saw it.
// The decisions are pure; `sendImportFailureReport` takes its GraphQL client and upload as
// arguments, and `ImportFailureReporter` its question to the user, so they're unit-tested
// with fakes (importFailureReport.test.ts).

import SparkMD5 from 'spark-md5';
import type { ImportPreview } from './importTypes';
import type { ImportFileEntry } from './importScreenState';
import type { Translate } from './importTexts';

export type ImportFailureStage = 'read' | 'save';
export type ImportFailureCode = 'not_readable' | 'save_failed';

/** The server's ImportFailureReport::MAX_FILE_BYTES: larger files are reported without the file. */
export const MAX_REPORTED_FILE_BYTES = 20 * 1024 * 1024;
/** The server's ImportFailureReport::MAX_DETAIL_LENGTH. */
export const MAX_DETAIL_LENGTH = 2000;
/** generateDirectUpload's purpose for these files: the server keeps them on the private bucket. */
export const IMPORT_FAILURE_PURPOSE = 'import_failure';

/** Why a read file is worth sending in, or null when it read fine or the failure says it all. */
export function readFailureCode(preview: ImportPreview | null): ImportFailureCode | null {
  return preview?.error === 'not_readable' ? 'not_readable' : null;
}

export function truncatedDetail(detail: string | null | undefined): string | null {
  const text = (detail ?? '').trim();
  return text ? text.slice(0, MAX_DETAIL_LENGTH) : null;
}

const CONTENT_TYPES: Record<string, string> = {
  gpx: 'application/gpx+xml',
  kml: 'application/vnd.google-earth.kml+xml',
  kmz: 'application/vnd.google-earth.kmz',
  geojson: 'application/geo+json',
  json: 'application/json',
  tcx: 'application/vnd.garmin.tcx+xml',
  zip: 'application/zip'
};

export const extensionOf = (name: string): string | null => {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : null;
};

export const contentTypeFor = (name: string): string => CONTENT_TYPES[extensionOf(name) ?? ''] ?? 'application/octet-stream';

export function md5Base64(bytes: Uint8Array): string {
  const spark = new SparkMD5.ArrayBuffer();
  spark.append(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer);
  const hex = spark.end();
  return btoa(String.fromCharCode(...(hex.match(/.{2}/g) ?? []).map(h => parseInt(h, 16))));
}

export interface ImportFailureReport {
  /** The file as the user added it: for a file out of a ZIP, the ZIP. */
  fileName: string;
  /** The file, when the user agreed to send it. */
  bytes: Uint8Array | null;
  /** The file's size, sent either way. */
  byteSize: number | null;
  /** The user was asked to send the file and said no. */
  fileDeclined?: boolean;
  stage: ImportFailureStage;
  errorCode: ImportFailureCode;
  /** The raw error, plus which file inside a ZIP it was. */
  errorDetail?: string | null;
  /** The format the reader saw, when it got that far. */
  fileFormat?: string | null;
}

/** The reportImportFailure input for `report`, given the upload's signed id (null: not uploaded). */
export function reportInput(report: ImportFailureReport, signedBlobId: string | null) {
  const input: Record<string, unknown> = {
    platform: 'web',
    fileName: report.fileName,
    fileFormat: report.fileFormat ?? extensionOf(report.fileName),
    stage: report.stage,
    errorCode: report.errorCode
  };
  const byteSize = report.byteSize ?? report.bytes?.byteLength ?? null;
  if (byteSize !== null) input.byteSize = byteSize;
  const detail = truncatedDetail(report.errorDetail);
  if (detail) input.errorDetail = detail;
  if (signedBlobId) input.signedBlobId = signedBlobId;
  if (report.fileDeclined) input.fileDeclined = true;
  return input;
}

export interface ImportFailureReportDeps {
  /** graph_client.request */
  request: (document: unknown, variables: Record<string, unknown>) => Promise<any>;
  /** PUT to the presigned URL; throws when storage turns it away. */
  put: (url: string, headers: Record<string, string>, body: Uint8Array) => Promise<void>;
  mutations: { generateDirectUpload: unknown; reportImportFailure: unknown };
}

/**
 * Uploads the file (when the user agreed and it's small enough) and reports it. A failed
 * upload still sends the report, without the file. Never throws.
 */
export async function sendImportFailureReport(report: ImportFailureReport, deps: ImportFailureReportDeps): Promise<boolean> {
  try {
    const signedBlobId = await uploadFailedFile(report, deps).catch(() => null);
    const res = await deps.request(deps.mutations.reportImportFailure, { input: reportInput(report, signedBlobId) });
    return res?.reportImportFailure?.success === true;
  } catch {
    return false;
  }
}

/** The upload's signed id, or null when there's no file to send. Throws when the upload fails. */
async function uploadFailedFile(report: ImportFailureReport, deps: ImportFailureReportDeps): Promise<string | null> {
  const bytes = report.fileDeclined ? null : report.bytes;
  if (!bytes || bytes.byteLength > MAX_REPORTED_FILE_BYTES) return null;
  const contentType = contentTypeFor(report.fileName);
  const res = await deps.request(deps.mutations.generateDirectUpload, {
    input: {
      filename: report.fileName,
      byteSize: bytes.byteLength,
      contentType,
      checksumBase64: md5Base64(bytes),
      purpose: IMPORT_FAILURE_PURPOSE
    }
  });
  const upload = res?.generateDirectUpload;
  if (!upload?.uploadUrl) return null;
  await deps.put(upload.uploadUrl, { ...JSON.parse(upload.headers || '{}'), 'Content-Type': contentType }, bytes);
  return upload.signedId ?? null;
}

/** A report still to send: everything but the file, read from `source` only when it goes. */
export type ImportFailurePlan = Omit<ImportFailureReport, 'bytes' | 'byteSize' | 'fileDeclined'> & { key: string; source: string };

/** Which file inside a ZIP failed, for a file that came out of one. */
const archiveDetail = (entry: ImportFileEntry, sourceName: string): string | null =>
  entry.source !== entry.id ? `File in archive: ${entry.name} (archive ${sourceName})` : null;

const joinedDetail = (...parts: Array<string | null>): string | null => parts.filter(Boolean).join('\n') || null;

/** The read failures among the entries read from the file `source` (the user's file, named `sourceName`). */
export function readFailurePlans(entries: ImportFileEntry[], source: string, sourceName: string): ImportFailurePlan[] {
  return entries.flatMap(entry => {
    if (entry.source !== source) return [];
    const errorCode = readFailureCode(entry.preview);
    if (!errorCode) return [];
    return [{
      key: entry.id,
      source,
      fileName: sourceName,
      stage: 'read' as const,
      errorCode,
      errorDetail: archiveDetail(entry, sourceName),
      fileFormat: entry.preview?.format ?? null
    }];
  });
}

/** One plan per file the import button failed to save. */
export function saveFailurePlans(
  failures: Array<{ id: string; message: string }>,
  entries: ImportFileEntry[],
  sourceName: (source: string) => string | null
): ImportFailurePlan[] {
  return failures.flatMap(failure => {
    const entry = entries.find(e => e.id === failure.id);
    const name = entry ? sourceName(entry.source) : null;
    if (!entry || !name) return [];
    return [{
      key: entry.id,
      source: entry.source,
      fileName: name,
      stage: 'save' as const,
      errorCode: 'save_failed' as const,
      errorDetail: joinedDetail(failure.message, archiveDetail(entry, name)),
      fileFormat: entry.preview?.format ?? null
    }];
  });
}

/** At most this many file names are listed in the question; the rest are an ellipsis. */
export const MAX_PROMPT_NAMES = 5;

export interface ImportFailurePromptCopy {
  title: string;
  message: string;
  /** Listed under the message when there are several files. */
  names: string[];
  /** More files than `names` shows. */
  more: boolean;
}

/** The question for these files: one names its file in the message, several are listed. */
export function failurePromptCopy(t: Translate, fileNames: string[]): ImportFailurePromptCopy {
  if (fileNames.length === 1) {
    return {
      title: t('import_files.failure_report.title_one'),
      message: t('import_files.failure_report.message_one', { name: fileNames[0] }),
      names: [],
      more: false
    };
  }
  return {
    title: t('import_files.failure_report.title_many'),
    message: t('import_files.failure_report.message_many'),
    names: fileNames.slice(0, MAX_PROMPT_NAMES),
    more: fileNames.length > MAX_PROMPT_NAMES
  };
}

/** A file the user added, as the reporter needs it: its size now, its bytes only if sent. */
export interface ImportFailureFile {
  size: number;
  bytes: () => Promise<Uint8Array>;
}

/**
 * Reports each failing file once per dialog, keyed by what failed and where. A file that
 * fails to read and is then removed and added again isn't reported twice. Each batch asks
 * the user one question first.
 */
export class ImportFailureReporter {
  private readonly handled = new Set<string>();

  constructor(
    /** The user's file `source`; null when it's gone. */
    private readonly fileOf: (source: string) => ImportFailureFile | null,
    private readonly send: (report: ImportFailureReport) => Promise<unknown>,
    /** Asks whether to send these files (by name); true for "Send". */
    private readonly ask: (fileNames: string[]) => Promise<boolean>
  ) {}

  /**
   * Asks about the plans not reported yet, then reports them: with their files on a yes,
   * without on a no. Resolves once the user has answered; `sent` once the reports are out.
   */
  async report(plans: ImportFailurePlan[]): Promise<{ sent: Promise<void> }> {
    const fresh = plans.filter(p => !this.handled.has(`${p.stage}:${p.key}`));
    fresh.forEach(p => this.handled.add(`${p.stage}:${p.key}`));
    if (!fresh.length) return { sent: Promise.resolve() };

    const names = Array.from(new Set(fresh.map(p => p.fileName)));
    const sendFiles = await this.ask(names).catch(() => false);
    const sent = Promise.all(fresh.map(async ({ key: _key, source, ...rest }) => {
      try {
        const file = this.fileOf(source);
        if (!file) return;
        const bytes = sendFiles ? await file.bytes() : null;
        await this.send({ ...rest, bytes, byteSize: file.size, fileDeclined: !sendFiles });
      } catch {
        // A report that can't be sent changes nothing for the user.
      }
    })).then(() => undefined);
    return { sent };
  }
}
